// ABOUTME: Authentication context provider using a reducer-based state machine
// ABOUTME: Manages auth state (initializing/authenticated/unauthenticated) and exposes login/logout/register

import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { User, AuthResponse } from '../types/api';
import { authAPI, getCurrentUserProfile, endImpersonation, setImpersonatedUserId, getCurrentImpersonatedUserId, setCurrentAccessToken } from '../services/api';

// Auth state machine types
export type AuthState =
  | { status: 'initializing' }
  | { status: 'authenticated'; user: User }
  | { status: 'unauthenticated' };

export type AuthAction =
  | { type: 'INITIALIZE_AUTHENTICATED'; user: User }
  | { type: 'INITIALIZE_UNAUTHENTICATED' }
  | { type: 'LOGIN_SUCCESS'; user: User }
  | { type: 'REGISTER_SUCCESS'; user: User }
  | { type: 'SET_AUTH'; user: User }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_USER'; user: User };

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INITIALIZE_AUTHENTICATED':
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
    case 'SET_AUTH':
      return { status: 'authenticated', user: action.user };
    case 'INITIALIZE_UNAUTHENTICATED':
    case 'LOGOUT':
      return { status: 'unauthenticated' };
    case 'REFRESH_USER':
      if (state.status === 'authenticated') {
        return { status: 'authenticated', user: action.user };
      }
      return state;
    default:
      return state;
  }
}

// Shape of the user object passed into setAuth by OAuth/invitation callback flows,
// which build it from a raw profile fetch (`id`) rather than the `User` type (`userId`).
interface SetAuthUserData {
  id: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  isAdmin?: boolean;
  hasFamily?: boolean;
  emailVerificationStatus?: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  setAuth: (authData: { isAuthenticated: boolean; user: SetAuthUserData; token: string }) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Shape returned by GET /api/user/profile — uses `id` rather than the `User` type's `userId`.
interface UserProfileResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  isAdmin?: boolean;
  emailVerificationStatus?: string;
  createdAt?: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, { status: 'initializing' });

  useEffect(() => {
    if (state.status !== 'initializing') return;

    // Attempt a silent refresh using the HttpOnly refresh token cookie.
    // If it succeeds we have a valid access token and can show the user their session.
    // If it fails (no cookie, or expired) we treat them as unauthenticated.
    const initializeAuth = async () => {
      try {
        const refreshResult = await authAPI.refreshToken();
        if (!refreshResult) {
          dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
          return;
        }

        // Prefer the cached user object to avoid an extra network hop.
        // If it's missing (e.g. localStorage was cleared), fetch from the API.
        const cachedUser = authAPI.getCurrentUser();
        if (cachedUser) {
          dispatch({ type: 'INITIALIZE_AUTHENTICATED', user: cachedUser });
          return;
        }

        try {
          const profile = await getCurrentUserProfile() as UserProfileResponse;
          if (profile) {
            const user: User = {
              userId: profile.id,
              email: profile.email,
              firstName: profile.firstName,
              lastName: profile.lastName,
              profileImageUrl: profile.profileImageUrl,
              isAdmin: profile.isAdmin,
              emailVerificationStatus: profile.emailVerificationStatus,
              createdAt: profile.createdAt,
            };
            localStorage.setItem('user', JSON.stringify(user));
            dispatch({ type: 'INITIALIZE_AUTHENTICATED', user });
          } else {
            dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
          }
        } catch {
          dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
        }
      } catch {
        dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
      }
    };

    initializeAuth();
  }, [state.status]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });

      authAPI.setAuth(response);

      const newUser: User = {
        userId: response.userId,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        profileImageUrl: response.profileImageUrl,
        isAdmin: response.isAdmin,
        emailVerificationStatus: response.emailVerificationStatus,
        createdAt: response.createdAt,
      };

      dispatch({ type: 'LOGIN_SUCCESS', user: newUser });
      return response; // Return full response for nextAction routing
    } catch (error) {
      console.error('Login failed:', error);
      if (state.status === 'initializing') {
        dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
      }
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const response = await authAPI.register({ email, password, firstName, lastName });
      authAPI.setAuth(response);

      const newUser: User = {
        userId: response.userId,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        profileImageUrl: response.profileImageUrl,
        isAdmin: response.isAdmin,
        emailVerificationStatus: response.emailVerificationStatus,
        createdAt: response.createdAt,
      };

      dispatch({ type: 'REGISTER_SUCCESS', user: newUser });
      return response; // Return full response for nextAction routing
    } catch (error) {
      console.error('Registration failed:', error);
      if (state.status === 'initializing') {
        dispatch({ type: 'INITIALIZE_UNAUTHENTICATED' });
      }
      throw error;
    }
  };

  const logout = async () => {
    // If actively impersonating, clear the header first then call end endpoint for audit log (best effort)
    const impersonatedUserId = getCurrentImpersonatedUserId();
    if (impersonatedUserId !== null) {
      setImpersonatedUserId(null);
      try {
        await endImpersonation(impersonatedUserId);
      } catch (ex) {
        console.warn('Failed to record impersonation end on logout — proceeding with logout anyway', ex);
      }
    }
    authAPI.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const setAuth = (authData: { isAuthenticated: boolean; user: SetAuthUserData; token: string }) => {
    if (authData.isAuthenticated && authData.token) {
      setCurrentAccessToken(authData.token);
      localStorage.setItem('user', JSON.stringify({
        userId: authData.user.id,
        email: authData.user.email || '',
        firstName: authData.user.firstName || '',
        lastName: authData.user.lastName || '',
        profileImageUrl: authData.user.profileImageUrl,
        isAdmin: authData.user.isAdmin,
        hasFamily: authData.user.hasFamily || false,
        emailVerificationStatus: authData.user.emailVerificationStatus || 'NotVerified',
        createdAt: authData.user.createdAt,
      }));

      dispatch({
        type: 'SET_AUTH',
        user: {
          userId: authData.user.id,
          email: authData.user.email || '',
          firstName: authData.user.firstName || '',
          lastName: authData.user.lastName || '',
          profileImageUrl: authData.user.profileImageUrl,
          isAdmin: authData.user.isAdmin,
          emailVerificationStatus: authData.user.emailVerificationStatus || 'NotVerified',
          createdAt: authData.user.createdAt,
        },
      });
    }
  };

  const refreshUser = async () => {
    try {
      // Fetch fresh user data from API
      const userProfile = await getCurrentUserProfile() as UserProfileResponse;
      if (userProfile) {
        const updatedUser: User = {
          userId: userProfile.id,
          email: userProfile.email,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          profileImageUrl: userProfile.profileImageUrl,
          isAdmin: userProfile.isAdmin,
          emailVerificationStatus: userProfile.emailVerificationStatus,
          createdAt: userProfile.createdAt,
        };

        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(updatedUser));

        dispatch({ type: 'REFRESH_USER', user: updatedUser });
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // Fallback to localStorage if API call fails
      const currentUser = authAPI.getCurrentUser();
      if (currentUser) {
        dispatch({ type: 'REFRESH_USER', user: currentUser });
      }
    }
  };

  const value = {
    user: state.status === 'authenticated' ? state.user : null,
    isLoading: state.status === 'initializing',
    login,
    register,
    logout,
    setAuth,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};